<template>
  <div v-if="reviews.length > 0" class="ppc-reviews-section md:my-16 my-8 px-4">
    <h2 class="text-left mb-10 mx-4 md:mx-16">Trusted by {{ location ? location + ' ' : '' }}Neighbors</h2>
    <div class="review-slider mx-4 md:mx-16">
      <ClientOnly>
        <Carousel
          :items-to-show="slidesPerView"
          :items-to-scroll="1"
          :autoplay="4000"
          :wrapAround="true"
          :pauseAutoplayOnHover="true"
        >
          <Slide v-for="review in reviews" :key="review.id">
            <PPCReviewCard :review="review" class="carousel-slide" />
          </Slide>
          <template #addons>
            <Navigation />
          </template>
        </Carousel>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import { Carousel, Slide, Navigation } from "vue3-carousel";
import "vue3-carousel/dist/carousel.css";
import PPCReviewCard from "./PPCReviewCard.vue";

interface Review {
  id: number | string;
  name: string;
  rating: number;
  testimonial: string;
  image: string | null;
}

const props = defineProps({
  location: {
    type: String,
    default: "",
  },
  // Franchise branch code (utm_content, e.g. "AMU010") used to filter reviews.
  branchCode: {
    type: String,
    default: "",
  },
});

// Pull live reviews from Birdeye (SSR-friendly). Section is hidden when empty.
const { data: fetchedReviews } = await useAsyncData<Review[]>(
  `ppc-birdeye-reviews-${props.branchCode || 'all'}`,
  () =>
    $fetch<Review[]>("/api/reviews/birdeye", {
      params: { count: 8, branchCode: props.branchCode || undefined },
    }),
  { default: () => [], watch: [() => props.branchCode] }
);

const reviews = computed<Review[]>(() => fetchedReviews.value ?? []);

const slidesPerView = ref(3);

const updateSlidesPerView = () => {
  slidesPerView.value = window.innerWidth < 720 ? 1 : 3;
};

onMounted(() => {
  updateSlidesPerView();
  window.addEventListener("resize", updateSlidesPerView);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateSlidesPerView);
});
</script>

<style scoped>
.ppc-reviews-section {
  background: #fff;
}

:deep(.carousel__slide) {
  align-items: stretch;
  padding: 0 8px;
}

:deep(.carousel-slide) {
  width: 100%;
}

:deep(.carousel__prev),
:deep(.carousel__next) {
  color: #4da43a;
}
</style>
